// The passport certificate list (BSI master list) is checked daily. A new
// list is staged here as a candidate; nothing goes live until it is approved.
// Roll back restores only the passport list and keeps every other setting.

import { el, clear, fmtInt, relativeTime } from '../ui/dom';
import { renderShell, renderError, renderLoading } from '../ui/shell';
import { ApiError, UnauthorizedError } from '../api/client';
import { navigate } from '../router';
import type { RouteContext } from '../router';
import {
  approvePassportCertList,
  checkPassportCertListNow,
  getPassportCertListOverview,
  probePassportCertList,
  rejectPassportCertList,
  rollbackPassportCertList,
  type CandidateView,
  type PassportCertListOverview,
  type ProbeResult,
  type ProbeSide,
  type PullRequestResult,
  type ReportCert,
} from '../api/passport-cert-list';
import {
  candidateActions,
  candidateHeadline,
  certLine,
  certStillValid,
  countryGroups,
  probeLabel,
  safeGithubUrl,
  statusLabel,
  type CandidateAction,
} from './passport-certificates-support';

const CONFIRMS: Record<CandidateAction, (c: CandidateView) => string> = {
  approve: (c) =>
    `Approve list ${c.version}? It goes live to the app right away, and pull requests are opened to bundle it.`,
  reject: (c) => `Reject list ${c.version}? It will not go live.`,
  rollback: (c) =>
    `Roll back list ${c.version}? This restores only the passport list to the previous one and keeps every other setting.`,
};

const RUNNERS: Record<CandidateAction, (id: string) => Promise<CandidateView>> = {
  approve: approvePassportCertList,
  reject: rejectPassportCertList,
  rollback: rollbackPassportCertList,
};

const ACTION_LABELS: Record<CandidateAction, string> = {
  approve: 'Approve',
  reject: 'Reject',
  rollback: 'Roll back',
};

export async function renderPassportCertificates({ root }: RouteContext): Promise<void> {
  const content = renderShell(root, 'passport-list');
  const notice = el('div', { class: 'view-caption' });
  const results = el('div', { class: 'results' });
  let busy = false;
  let workingId: string | null = null;
  let workingAction: CandidateAction | null = null;

  content.append(
    el('div', { class: 'page-head' }, [el('h1', {}, ['Passport certificate list'])]),
    el('p', { class: 'view-caption' }, [
      'The list is checked once a day. A new list waits here until you approve it; nothing changes in the app before that.',
    ]),
    notice,
    results,
  );

  function showNotice(text: string, isError = false): void {
    clear(notice);
    if (!text) return;
    notice.append(el('div', { class: isError ? 'pill pill-danger' : 'pill pill-info', textContent: text }));
  }

  function handleError(err: unknown, fallback: string): void {
    if (err instanceof UnauthorizedError) {
      navigate('/login');
      return;
    }
    const message = err instanceof Error && err.message ? err.message : fallback;
    showNotice(err instanceof ApiError && err.status === 409 ? `Not done: ${message}` : message, true);
  }

  async function load(): Promise<void> {
    try {
      const overview = await getPassportCertListOverview();
      render(overview);
    } catch (err) {
      if (err instanceof UnauthorizedError) return navigate('/login');
      renderError(results, err instanceof Error ? err.message : 'Failed to load the passport list.');
    }
  }

  function setBusy(on: boolean): void {
    busy = on;
    for (const b of results.querySelectorAll<HTMLButtonElement>('button')) b.disabled = on;
  }

  async function runAction(c: CandidateView, action: CandidateAction): Promise<void> {
    if (busy) return;
    if (!window.confirm(CONFIRMS[action](c))) return;
    workingId = c.id;
    workingAction = action;
    setBusy(true);
    markWorking();
    try {
      await RUNNERS[action](c.id);
      showNotice(`${ACTION_LABELS[action]} done for ${c.version}.`);
    } catch (err) {
      handleError(err, `Failed to ${ACTION_LABELS[action].toLowerCase()} that list.`);
    }
    try {
      await load();
    } finally {
      workingId = null;
      workingAction = null;
      setBusy(false);
    }
  }

  function markWorking(): void {
    for (const b of results.querySelectorAll<HTMLButtonElement>('button[data-cid]')) {
      if (b.dataset.cid === workingId && b.dataset.action === workingAction) b.textContent = 'Working…';
    }
  }

  async function checkNow(button: HTMLButtonElement): Promise<void> {
    if (busy) return;
    setBusy(true);
    button.textContent = 'Checking…';
    try {
      const r = await checkPassportCertListNow();
      showNotice(r.message);
    } catch (err) {
      handleError(err, 'The check failed.');
    }
    try {
      await load();
    } finally {
      setBusy(false);
    }
  }

  async function testConnections(button: HTMLButtonElement): Promise<void> {
    if (busy) return;
    setBusy(true);
    button.textContent = 'Testing…';
    try {
      showProbe(await probePassportCertList());
    } catch (err) {
      handleError(err, 'The connection test failed.');
    } finally {
      button.textContent = 'Test connections';
      setBusy(false);
    }
  }

  function showProbe(r: ProbeResult): void {
    clear(notice);
    notice.append(probeLine('Spaces', r.spaces), probeLine('GitHub', r.github));
  }

  function render(o: PassportCertListOverview): void {
    clear(results);
    results.append(liveCard(o), watchLine(o, checkNow, testConnections));
    if (o.candidates.length === 0) {
      results.append(el('div', { class: 'empty' }, ['No lists have been found yet.']));
      return;
    }
    results.append(el('div', { class: 'section-head' }, [el('h2', {}, ['Candidates'])]));
    for (const c of o.candidates) results.append(candidateCard(c, runAction));
  }

  renderLoading(results, 'Loading passport list…');
  await load();
}

function liveCard(o: PassportCertListOverview): HTMLElement {
  const live = o.live;
  const rows: [string, string][] = live
    ? [
        ['Version', live.passportCertListVersion ?? '—'],
        ['Size', live.passportCertListSizeBytes == null ? '—' : `${fmtInt(live.passportCertListSizeBytes)} bytes`],
        ['SHA-256', live.passportCertListSHA256 ? `${live.passportCertListSHA256.slice(0, 12)}…` : '—'],
      ]
    : [];
  const live0 = o.candidates.find((c) => c.isLive);
  if (live && live0) rows.splice(1, 0, ['Certificates', fmtInt(live0.certificateCount)]);
  return el('div', { class: 'card' }, [
    el('div', { class: 'card-label' }, ['Live now']),
    live
      ? el('div', {}, rows.map(([k, v]) => el('div', {}, [el('span', { class: 'cell-muted', textContent: `${k}: ` }), v])))
      : el('div', { class: 'cell-muted' }, ['No list is configured.']),
  ]);
}

function watchLine(
  o: PassportCertListOverview,
  onCheck: (b: HTMLButtonElement) => void,
  onTest: (b: HTMLButtonElement) => void,
): HTMLElement {
  const w = o.watch;
  const parts = [w.lastCheckedAt ? `Last checked ${relativeTime(w.lastCheckedAt)}` : 'Not checked yet'];
  if (w.consecutiveFailures > 0) {
    parts.push(`failing ${w.consecutiveFailures} ${w.consecutiveFailures === 1 ? 'time' : 'times'}: ${w.lastError ?? 'unknown error'}`);
  }
  const button = el('button', { class: 'btn btn-sm', type: 'button' }, ['Check now']);
  button.addEventListener('click', () => onCheck(button));
  const test = el('button', { class: 'btn btn-sm btn-ghost', type: 'button' }, ['Test connections']);
  test.addEventListener('click', () => onTest(test));
  return el('div', { class: 'controls' }, [
    el('div', { class: w.consecutiveFailures > 0 ? 'pill pill-danger' : 'cell-muted', textContent: parts.join(' · ') }),
    el('div', { class: 'controls-right' }, [test, button]),
  ]);
}

function probeLine(name: string, side: ProbeSide): HTMLElement {
  return el('div', {}, [
    el('span', { class: side.ok ? 'pill pill-ok' : 'pill pill-danger', textContent: probeLabel(name, side) }),
    ' ',
    el('span', { textContent: side.detail }),
  ]);
}

function statusPill(c: CandidateView): HTMLElement {
  const cls =
    c.status === 'approved' || c.status === 'already_live'
      ? 'pill pill-ok'
      : c.status === 'blocked' || c.status === 'rejected'
        ? 'pill pill-danger'
        : c.status === 'pending'
          ? 'pill pill-warn'
          : 'pill';
  return el('span', { class: cls, textContent: statusLabel(c.status) });
}

function candidateCard(
  c: CandidateView,
  onAction: (c: CandidateView, a: CandidateAction) => void,
): HTMLElement {
  const body: (HTMLElement | null)[] = [
    el('div', { class: 'cell-strong', textContent: candidateHeadline(c) }),
    el('div', {}, [statusPill(c), c.isLive ? el('span', { class: 'pill pill-ok', textContent: 'Live' }) : null]),
  ];

  if (c.decidedAt) {
    body.push(
      el('div', { class: 'cell-muted', textContent: `Decided ${relativeTime(c.decidedAt)}${c.decidedByEmail ? ` by ${c.decidedByEmail}` : ''}` }),
    );
  }

  if (c.checks.hardStops.length > 0) {
    body.push(messageList('Stopped by', c.checks.hardStops.map((m) => m.message), 'pill-danger'));
  }
  if (c.checks.warnings.length > 0) {
    body.push(messageList('Warnings', c.checks.warnings.map((m) => m.message), 'pill-warn'));
  }
  if (c.checks.passed.length > 0) {
    body.push(messageList('Checks passed', c.checks.passed, 'pill-ok'));
  }

  if (c.report) {
    body.push(
      certGroups('Added', c.report.added, false),
      certGroups('Removed', c.report.removed, true),
      c.report.newCountries.length ? textLine(`New countries: ${c.report.newCountries.join(', ')}`) : null,
      c.report.removedCountries.length ? textLine(`Removed countries: ${c.report.removedCountries.join(', ')}`) : null,
      c.report.expired.length ? textLine(`Already expired in this list: ${c.report.expired.length}`) : null,
    );
  }

  if (c.pullRequests && c.pullRequests.length > 0) {
    body.push(el('div', {}, [el('div', { class: 'cell-muted', textContent: 'Pull requests' }), ...c.pullRequests.map(prLine)]));
  }

  const actions = candidateActions(c);
  if (actions.length > 0) {
    body.push(
      el(
        'div',
        { class: 'button-row', style: 'justify-content:flex-start' },
        actions.map((a) =>
          el(
            'button',
            {
              class: `btn btn-sm${a === 'approve' ? '' : ' btn-ghost'}${a === 'reject' ? ' btn-danger' : ''}`,
              type: 'button',
              dataset: { cid: c.id, action: a },
              onClick: () => onAction(c, a),
            },
            [ACTION_LABELS[a]],
          ),
        ),
      ),
    );
  }

  return el('div', { class: 'card', style: 'display:flex;flex-direction:column;gap:8px;margin-bottom:12px' }, body);
}

function textLine(text: string): HTMLElement {
  return el('div', { class: 'cell-muted', textContent: text });
}

function messageList(title: string, items: string[], pill: string): HTMLElement {
  return el('div', {}, [
    el('span', { class: `pill ${pill}`, textContent: title }),
    el('ul', {}, items.map((m) => el('li', { textContent: m }))),
  ]);
}

function certGroups(title: string, certs: ReportCert[], markStillValid: boolean): HTMLElement | null {
  if (certs.length === 0) return null;
  const now = new Date();
  return el('div', {}, [
    el('div', { class: 'cell-muted', textContent: `${title} (${certs.length})` }),
    ...countryGroups(certs).map((g) =>
      el('details', {}, [
        el('summary', { textContent: `${g.name} (${g.country}) ${g.count}` }),
        el(
          'ul',
          {},
          g.certs.map((cert) =>
            el('li', {}, [
              el('span', { textContent: certLine(cert) }),
              markStillValid && certStillValid(cert.validTo, now)
                ? el('span', { class: 'pill pill-danger', style: 'margin-left:6px', textContent: 'still valid' })
                : null,
            ]),
          ),
        ),
      ]),
    ),
  ]);
}

function prLine(pr: PullRequestResult): HTMLElement {
  if (pr.outcome === 'failed') {
    return el('div', { class: 'pill pill-danger', textContent: `${pr.base}: failed — ${pr.error ?? 'unknown error'}` });
  }
  const label = pr.outcome === 'opened' ? 'opened' : pr.outcome === 'already_open' ? 'already open' : 'already current';
  const safe = safeGithubUrl(pr.url);
  const link = safe
    ? el('a', { href: safe, target: '_blank', rel: 'noopener noreferrer', textContent: safe })
    : pr.url
      ? el('span', { textContent: pr.url })
      : null;
  return el('div', {}, [`${pr.base}: ${label} `, link]);
}
